import * as React from 'react';
import { useGoogleMapsApi } from './useGoogleMapsApi';
import { DEBUG } from 'lib/util/debug';
interface GeolocationState {
  position: google.maps.LatLng | null;
  updatedAt: number | null;
  error: GeolocationPositionError | null;
}

type Listener = (state: GeolocationState) => void;

class GeolocationWatcher {
  private static instance: GeolocationWatcher;
  private listeners: Set<Listener> = new Set();
  private watchId: number | null = null;
  private currentState: GeolocationState = {
    position: null,
    updatedAt: null,
    error: null,
  };

  private constructor() {
    // Private constructor for singleton
  }

  public static getInstance(): GeolocationWatcher {
    if (!GeolocationWatcher.instance) {
      GeolocationWatcher.instance = new GeolocationWatcher();
    }
    return GeolocationWatcher.instance;
  }

  public subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    // Immediately notify new listener of current state
    listener(this.currentState);

    // Start watching if this is the first listener
    if (this.listeners.size === 1) {
      this.startWatching();
    }

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
      // Stop watching if there are no more listeners
      if (this.listeners.size === 0) {
        this.stopWatching();
      }
    };
  }

  private notify(state: GeolocationState): void {
    this.currentState = state;
    DEBUG.log('GeolocationWatcher', {
      position: state.position
        ? {
            lat: state.position.lat(),
            lng: state.position.lng(),
          }
        : null,
      updatedAt: state.updatedAt,
      error: state.error?.message,
    });
    this.listeners.forEach((listener) => listener(state));
  }

  private startWatching(): void {
    if (this.watchId !== null) return;

    DEBUG.log('GeolocationWatcher', 'Starting position watch');
    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        const newPosition = new google.maps.LatLng(
          position.coords.latitude,
          position.coords.longitude
        );
        this.notify({
          position: newPosition,
          updatedAt: Date.now(),
          error: null,
        });
      },
      (error) => {
        DEBUG.log('GeolocationWatcher', {
          error: error.message,
          code: error.code,
        });
        this.notify({
          ...this.currentState,
          error,
        });
      },
      { enableHighAccuracy: true }
    );
  }

  private stopWatching(): void {
    if (this.watchId !== null) {
      DEBUG.log('GeolocationWatcher', 'Stopping position watch');
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
  }
}

export const useCurrentPosition = () => {
  const { isLoaded } = useGoogleMapsApi();
  const [state, setState] = React.useState<GeolocationState>({
    position: null,
    updatedAt: null,
    error: null,
  });

  // Create ref that will be updated with the latest position
  const currentPositionRef = React.useRef<google.maps.LatLng | null>(null);

  React.useEffect(() => {
    if (!isLoaded) return;

    const watcher = GeolocationWatcher.getInstance();
    const unsubscribe = watcher.subscribe((newState) => {
      setState(newState);
      // Update ref with latest position
      currentPositionRef.current = newState.position;
    });

    return () => {
      unsubscribe();
    };
  }, [isLoaded]);

  return {
    currentPosition: state.position,
    currentPositionRef,
    updatedAt: state.updatedAt,
    error: state.error,
    isLoaded,
  };
};
